import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import {
  LogOut,
  User,
  BrainCircuit,
  Bell,
  Trash2,
  CheckCheck,
  CalendarCheck,
} from "lucide-react";

import logo from "../assets/favicro.png";

const LabNavbar = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  // =========================
  // LOGOUT
  // =========================

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  // =========================
  // FETCH NOTIFICATIONS
  // =========================

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        "https://www.careplus.gopillz.com/lab-notifications",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Notification Error:", error);
    }
  };

  // =========================
  // MARK READ
  // =========================

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem("token");

      await axios.patch(
        `https://www.careplus.gopillz.com/labnotifications/${id}/read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNotifications((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, is_read: true }
            : item
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  // =========================
  // DELETE NOTIFICATION
  // =========================

  const deleteNotification = async (id) => {
    try {
      const token = localStorage.getItem("token");

      await axios.delete(
        `https://www.careplus.gopillz.com/lab_notifications/${id}/delete`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNotifications((prev) =>
        prev.filter((item) => item.id !== id)
      );
    } catch (error) {
      console.error(error);
    }
  };

  // =========================
  // CLEAR ALL
  // =========================

  const clearAllNotifications = async () => {
    try {
      const token = localStorage.getItem("token");

      await axios.delete(
        "https://www.careplus.gopillz.com/lab_notifications/clear-all",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNotifications([]);
    } catch (error) {
      console.error(error);
    }
  };

  // =========================
  // AUTO FETCH
  // =========================

  useEffect(() => {
    fetchNotifications();

    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // =========================
  // CLOSE OUTSIDE CLICK
  // =========================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }

      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () =>
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
  }, []);

  const unreadCount = notifications.filter(
    (n) => !n.is_read
  ).length;

  return (
    <nav className="bg-white shadow-md px-6 py-3 flex justify-between items-center">
      {/* ========================= */}
      {/* LOGO */}
      {/* ========================= */}

      <div className="flex items-center gap-2 text-2xl font-bold text-blue-600">
        <img
          src={logo}
          alt="AICA Logo"
          className="w-8 h-8 object-contain"
        />

        <span>
          AICA
          <span className="text-gray-700"> | Lab</span>
        </span>
      </div>

      {/* ========================= */}
      {/* RIGHT SIDE */}
      {/* ========================= */}

      <div className="flex items-center gap-4">

        {/* Dashboard Button */}

        {location.pathname !== "/lab_dashboard" && (
          <button
            onClick={() => navigate("/lab_dashboard")}
            className="flex items-center gap-2 text-gray-700 hover:text-blue-600"
          >
            <BrainCircuit className="w-5 h-5 text-blue-500" />

            <span className="hidden sm:inline">
              Lab Dashboard
            </span>
          </button>
        )}

        {/* Booking Requests */}

{location.pathname !== "/bookings" && (
  <button
    onClick={() => navigate("/bookings")}
    className="
      flex
      items-center
      gap-2
      px-4
      py-2
      rounded-2xl
      bg-green-50
      border
      border-green-100
      text-green-700
      hover:bg-green-100
      hover:scale-105
      transition-all
      duration-300
      shadow-sm
    "
  >

    <CalendarCheck className="w-5 h-5 text-green-500" />

    <span className="hidden sm:inline font-semibold">
      Booking Requests
    </span>

  </button>
)}

        {/* ========================= */}
        {/* NOTIFICATION */}
        {/* ========================= */}

        <div
          className="relative"
          ref={notificationRef}
        >
          <button
            onClick={() =>
              setNotificationOpen(!notificationOpen)
            }
            className="
              relative
              p-2
              rounded-xl
              bg-gray-100
              hover:bg-gray-200
              transition
            "
          >
            <Bell className="w-5 h-5 text-gray-700" />

            {unreadCount > 0 && (
              <span
                className="
                  absolute
                  -top-1
                  -right-1
                  bg-red-500
                  text-white
                  text-[10px]
                  font-bold
                  min-w-[18px]
                  h-[18px]
                  rounded-full
                  flex
                  items-center
                  justify-center
                "
              >
                {unreadCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}

          {notificationOpen && (
            <div
              className="
                absolute
                right-0
                mt-3
                w-[380px]
                bg-white
                rounded-2xl
                shadow-2xl
                border
                overflow-hidden
                z-50
              "
            >
              {/* Header */}

              <div className="p-4 border-b flex justify-between items-center">
                <div>
                  <h3 className="font-bold text-gray-800">
                    Notifications
                  </h3>

                  <p className="text-xs text-gray-500">
                    {unreadCount} unread
                  </p>
                </div>

                {notifications.length > 0 && (
                  <button
                    onClick={clearAllNotifications}
                    className="
                      text-red-600
                      hover:bg-red-50
                      p-2
                      rounded-lg
                    "
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              {/* Body */}

              <div className="max-h-[500px] overflow-y-auto">
  {notifications.length === 0 ? (
    <div className="p-8 text-center">
      <CheckCheck
        size={40}
        className="mx-auto text-blue-500"
      />

      <h4 className="mt-3 font-semibold text-gray-700">
        All Caught Up
      </h4>

      <p className="text-sm text-gray-500">
        No notifications available
      </p>
    </div>
  ) : (
    notifications.map((item) => (
  <div
    key={item.id}
    className={`
      group
      relative
      p-4
      border-b
      transition-all
      duration-300
      hover:bg-blue-50
      cursor-pointer
      ${
        item.is_read
          ? "bg-gray-50"
          : "bg-white"
      }
    `}
    onClick={() => {
      if (!item.is_read) {
        markAsRead(item.id);
      }

      if (item.route) {
        navigate(item.route);
      }

      setNotificationOpen(false);
    }}
  >
    {/* Header */}

    <div className="flex justify-between items-start">
      <div>
        <h4 className="font-semibold text-gray-800">
          {item.title || "Lab Request"}
        </h4>

        <p className="text-xs text-gray-500 mt-1">
          {item.message}
        </p>
      </div>

      {!item.is_read && (
        <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
      )}
    </div>

    {/* Patient Details */}

    <div className="mt-4 bg-gray-50 rounded-xl p-3 space-y-2">

      <div className="flex justify-between">
        <span className="text-gray-500 text-sm">
          Patient
        </span>

        <span className="font-medium text-sm text-gray-800">
          {item.data?.patient_name || "-"}
        </span>
      </div>

      <div className="flex justify-between">
        <span className="text-gray-500 text-sm">
          Phone
        </span>

        <span className="font-medium text-sm text-gray-800">
          {item.data?.patient_phone || "-"}
        </span>
      </div>

      <div className="flex justify-between">
        <span className="text-gray-500 text-sm">
          Test Type
        </span>

        <span className="font-medium text-sm text-gray-800">
          {item.data?.test_type || "-"}
        </span>
      </div>

      {item.data?.appointment_date && (
        <div className="flex justify-between">
          <span className="text-gray-500 text-sm">
            Appointment
          </span>

          <span className="font-medium text-sm text-gray-800">
            {new Date(
              item.data.appointment_date
            ).toLocaleString()}
          </span>
        </div>
      )}
    </div>

    {/* Test Names */}

    {item.data?.test_names?.length > 0 && (
      <div className="mt-4">
        <h5 className="font-semibold text-gray-800 mb-2">
          Selected Tests
        </h5>

        <ul className="space-y-2">
          {item.data.test_names.map(
            (test, index) => (
              <li
                key={index}
                className="
                  flex
                  items-center
                  gap-2
                  bg-blue-50
                  border
                  border-blue-100
                  rounded-lg
                  px-3
                  py-2
                  text-sm
                  text-blue-700
                "
              >
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>

                {typeof test === "object"
                  ? test.test_name
                  : test}
              </li>
            )
          )}
        </ul>
      </div>
    )}

    {/* Footer */}

    <div className="mt-4 flex justify-between items-center">

      <span
        className={`
          text-xs
          px-2
          py-1
          rounded-full
          ${
            item.priority === "high"
              ? "bg-red-100 text-red-700"
              : item.priority === "medium"
              ? "bg-yellow-100 text-yellow-700"
              : "bg-green-100 text-green-700"
          }
        `}
      >
        {item.priority || "normal"}
      </span>

      <button
        onClick={(e) => {
          e.stopPropagation();
          deleteNotification(item.id);
        }}
        className="
          text-red-500
          hover:text-red-700
          text-xs
          font-medium
        "
      >
        Delete
      </button>
    </div>
  </div>
))
  )}
</div>
            </div>
          )}
        </div>

        {/* ========================= */}
        {/* PROFILE */}
        {/* ========================= */}

        <div
          className="relative"
          ref={dropdownRef}
        >
          <button
            className="
              flex
              items-center
              gap-2
              text-gray-700
              hover:text-blue-600
            "
            onClick={() =>
              setDropdownOpen(!dropdownOpen)
            }
          >
            <User className="w-5 h-5" />

            <span className="hidden sm:inline">
              {localStorage.getItem(
                "lab_user_name"
              ) || "Lab User"}
            </span>
          </button>

          {dropdownOpen && (
            <div
              className="
                absolute
                right-0
                mt-2
                w-44
                bg-white
                border
                rounded-xl
                shadow-lg
                p-2
                z-50
              "
            >
              {location.pathname !== "/profile" && (
                <button
                  onClick={() =>
                    navigate("/profile")
                  }
                  className="
                    block
                    w-full
                    text-left
                    px-4
                    py-2
                    text-sm
                    text-gray-700
                    hover:bg-gray-100
                    rounded
                  "
                >
                  Profile
                </button>
              )}

              <button
                onClick={handleLogout}
                className="
                  block
                  w-full
                  text-left
                  px-4
                  py-2
                  text-sm
                  text-red-600
                  hover:bg-red-100
                  rounded
                  flex
                  items-center
                  gap-2
                "
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default LabNavbar;